import { View, ViewOpts, animate } from '@play-co/timestep-core/ui';
import { BuddiesCheckpoint } from './BuddiesCheckpoint';
import { BuddiesStrawberry } from './BuddiesStrawberry';
import { BuddiesGrandPrize } from './BuddiesGrandPrize';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import { Feature } from 'src/replicant/analytics';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import { BuddiesEventStatus } from 'src/replicant/buddies/buddies.getters';
import { assertNever } from 'src/replicant/utils';
import { buddiesEligibilityThreshold } from 'src/replicant/buddies/buddies.ruleset';
import sounds from 'src/lib/sounds';

type BuddiesProgressOpts = ViewOpts & {
  current: number;
  total: number;
  checkpoints: number[];
  onTapCheckpoint?: (checkpoint: BuddiesCheckpoint | BuddiesGrandPrize) => void;
  onClaim?: () => Promise<void>;
};

export class BuddiesProgress extends View {
  private bar: ProgressBar;
  private fillWidth: number;
  private current: number;
  private total: number;
  private checkpoints: BuddiesCheckpoint[] = [];
  private strawberry: BuddiesStrawberry;
  private grandPrize: BuddiesGrandPrize;
  private progressLabel: LangBitmapFontTextView;
  private nextLabel: LangBitmapFontTextView;
  private collectButton: ButtonScaleViewWithText;
  private canClaimRewards = false;

  constructor(opts: BuddiesProgressOpts) {
    super({
      ...opts,
      width: 600,
      height: 300,
      centerOnOrigin: true,
    });

    this.fillWidth = 405;
    this.current = -1;
    this.total = opts.total;

    this.bar = new ProgressBar({
      superview: this,
      domain: [0, ...opts.checkpoints, this.total],
      progress: 0,
      bgStyle: {
        image: 'assets/ui/buddies/popupBaking/progress_base.png',
        width: 567,
        height: 92,
        x: 14,
        y: 104,
      },
      fillStyle: {
        image: 'assets/ui/buddies/popupBaking/progress_fill.png',
        width: this.fillWidth,
        height: 40,
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 28, right: 4 },
          vertical: { top: 20, bottom: 20 },
        },
        paddingRight: 150,
        x: 72,
        y: 18,
      },
    });

    const spacing = this.fillWidth / (opts.checkpoints.length + 1);
    for (let i = 0; i < opts.checkpoints.length; i++) {
      const value = opts.checkpoints[i];
      const checkpoint = new BuddiesCheckpoint({
        superview: this,
        x: this.bar.style.x + 73 + spacing * (i + 1),
        y: 85,
        value,
        type: i + 1,
        index: i,
      });
      checkpoint.onInputStart = () => {
        sounds.sfx.play('move');
        opts.onTapCheckpoint?.(checkpoint);
      };
      this.checkpoints.push(checkpoint);
    }

    this.strawberry = new BuddiesStrawberry({
      superview: this,
      x: 80,
      y: 140,
    });

    this.grandPrize = new BuddiesGrandPrize({
      superview: this,
      x: 500,
      y: 80,
    });
    this.grandPrize.onInputStart = () => {
      opts.onTapCheckpoint?.(this.grandPrize);
    };

    this.progressLabel = new LangBitmapFontTextView({
      superview: this,
      x: 215,
      y: 215,
      align: 'center',
      verticalAlign: 'center',
      anchorY: 0.5,
      size: 46,
      font: 'Title',
      color: '#7FC0C7',
      width: 290,
      height: 50,
      centerOnOrigin: true,
    });

    this.nextLabel = new LangBitmapFontTextView({
      superview: this,
      x: this.progressLabel.style.x,
      y: this.progressLabel.style.y + 35,
      align: 'center',
      verticalAlign: 'center',
      anchorY: 0.5,
      size: 18,
      font: 'Body',
      color: '#FFFFFF',
      width: 290,
      height: 20,
      centerOnOrigin: true,
    });

    this.collectButton = new ButtonScaleViewWithText({
      superview: this,
      ...buttons.secondary,
      width: 300,
      height: 80,
      fontSize: 46,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: 215,
      y: 220,
      font: 'Body',
      visible: false,
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_COLLECT,
      },
      localeText: () => i18n('match2.buddies.bakingPopup.collect'),
      onClick: async () => opts?.onClaim?.(),
    });

    this.setProgress(this.current);
  }

  public setProgress(value: number) {
    // Prevent animation glitches if set the same value twice
    const current = Math.min(value, this.total);
    if (this.current !== current) {
      this.current = current;
      this.bar.animateProgress(this.current, 300, animate.linear, true);
    }

    for (let i = 0; i < this.checkpoints.length; i++) {
      const checkpoint = this.checkpoints[i];
      if (value >= checkpoint.value) {
        checkpoint.markAsChecked();
      }
    }

    const nextCheckpointValue = this.getNextCheckpointValue();
    this.progressLabel.localeText = () =>
      i18n('match2.buddies.bakingPopup.progress', {
        value: this.current,
        total: nextCheckpointValue,
      });

    const valueToNextReward = Math.max(nextCheckpointValue - this.current, 0);
    if (valueToNextReward > 0) {
      this.nextLabel.localeText = () =>
        i18n('match2.buddies.bakingPopup.nextReward', {
          value: valueToNextReward,
        });
    }
  }

  private getNextCheckpointValue() {
    for (let i = 0; i < this.checkpoints.length; i++) {
      const checkpoint = this.checkpoints[i];
      if (this.current < checkpoint.value) {
        return checkpoint.value;
      }
    }
    return this.total;
  }

  public updateEventStatus(status: BuddiesEventStatus, userScore?: number) {
    // Reset to default state
    this.collectButton.hide();
    this.progressLabel.show();
    this.nextLabel.show();
    this.progressLabel.updateOpts({
      y: 215,
    });

    switch (status) {
      case 'inProgress':
        this.eventInProgress();
        break;
      case 'overPendingScore':
        this.eventOverPendingScore(userScore ?? 0);
        break;
      case 'overPendingCollect':
        this.eventOverPendingCollect();
        break;
      case 'overClaimed':
      case 'overNoClaim':
      case 'unavailable':
        this.eventOver();
        break;
      default:
        assertNever(status);
    }
  }

  private eventInProgress() {
    const nextCheckpointValue = this.getNextCheckpointValue();
    const valueToNextReward = Math.max(nextCheckpointValue - this.current, 0);

    if (valueToNextReward > 0) {
      this.nextLabel.localeText = () =>
        i18n('match2.buddies.bakingPopup.nextReward', {
          value: valueToNextReward,
        });
    } else {
      this.nextLabel.localeText = () => '';
    }
  }

  private eventOverPendingScore(userScore: number) {
    const scoreToClaim = buddiesEligibilityThreshold - userScore;
    this.nextLabel.localeText = () =>
      i18n('match2.buddies.bakingPopup.scoreToClaim', {
        value: scoreToClaim,
      });
  }

  private eventOverPendingCollect() {
    this.collectButton.show();
    this.progressLabel.hide();
    this.nextLabel.hide();
  }

  private eventOver() {
    this.nextLabel.hide();
    this.collectButton.hide();
    this.progressLabel.show();
    this.progressLabel.updateOpts({
      y: 225,
    });
  }
}
